import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import {
    IconCameraEmptyDisabled,
    IconCloseCircle,
    IconEye,
    IconEyeOff,
    IconMicDisabled,
    IconMicrophone,
    IconShareDesktop,
    IconUserGroup
} from '../../base/icons';
import {
    isHiddenMeEnabled,
    requestParticipantHidden,
    requestParticipantKick,
    requestParticipantMute,
    requestParticipantRole
} from '../../webinar';

import { ParticipantContextMenu } from './ParticipantContextMenu';
import {
    ContextMenuIcon,
    ContextMenuItem,
    ContextMenuItemGroup
} from './styled';

/**
 * The actions a moderator can take on somebody in the OTHER room of the
 * meeting -- the main room, from backstage; backstage, from the main room.
 * Every control is a request relayed through the server, since the target is
 * not in the local conference. Positioned the same way as the meeting menu:
 * against the row that opened it, flipped above when it would run off the
 * bottom of the pane.
 *
 * @param {Object} props - Component props: the row's occupant, the roster it
 * came from (the live entry is re-read from it), the move action to offer
 * (send backstage / bring live) with its label key, and the menu callbacks.
 * @returns {JSX.Element}
 */
export const RosterContextMenu = ({
    moveAction,
    moveIcon = IconUserGroup,
    moveLabelKey,
    occupant: openedFor,
    offsetTarget,
    onEnter,
    onLeave,
    onSelect,
    roster
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const hiddenMeEnabled = useSelector(isHiddenMeEnabled);

    // The list passes the roster entry as it looked when the menu opened, but a
    // new object arrives whenever Prosody publishes a camera, microphone,
    // visibility, name or role change. Only render from that live entry so the
    // menu cannot retain stale controls after an update or departure.
    const occupant = roster.find(item => item.id === openedFor?.id);

    // Every control closes the row after firing: the row is redrawn from the
    // next server roster, and leaving the list open under a stale row invites a
    // second click on an action that has already happened.
    const runAction = useCallback(action => () => {
        onSelect();
        dispatch(action);
    }, [ dispatch, onSelect ]);

    if (!occupant) {
        return null;
    }

    const isModerator = occupant.role === 'moderator';
    const isPanelist = occupant.role === 'panelist';
    const isPresenter = occupant.role === 'presenter';

    return (
        <ParticipantContextMenu
            offsetTarget = { offsetTarget }
            onEnter = { onEnter }
            onLeave = { onLeave }
            onSelect = { onSelect }>
            <ContextMenuItemGroup>
                { (hiddenMeEnabled || occupant.hidden) && (
                    <ContextMenuItem
                        onClick = { runAction(requestParticipantHidden(occupant.id, !occupant.hidden)) }>
                        <ContextMenuIcon src = { occupant.hidden ? IconEye : IconEyeOff } />
                        <span>
                            { occupant.hidden
                                ? t('participantsPane.actions.showToOthers')
                                : t('participantsPane.actions.hideFromOthers') }
                        </span>
                    </ContextMenuItem>
                ) }
                { !isModerator && (
                    <ContextMenuItem
                        onClick = { runAction(requestParticipantRole(
                        occupant.id, isPanelist ? 'participant' : 'panelist')) }>
                        <ContextMenuIcon src = { isPanelist ? IconMicDisabled : IconMicrophone } />
                        <span>
                            { isPanelist
                                ? t('participantsPane.actions.revokePanelist')
                                : t('participantsPane.actions.grantPanelist') }
                        </span>
                    </ContextMenuItem>
                ) }
                { !isModerator && (
                    <ContextMenuItem
                        onClick = { runAction(requestParticipantRole(
                        occupant.id, isPresenter ? 'participant' : 'presenter')) }>
                        <ContextMenuIcon src = { IconShareDesktop } />
                        <span>
                            { isPresenter
                                ? t('participantsPane.actions.revokePresenter')
                                : t('participantsPane.actions.grantPresenter') }
                        </span>
                    </ContextMenuItem>
                ) }
                { !occupant.audioMuted && (
                    <ContextMenuItem
                        onClick = { runAction(requestParticipantMute(occupant.id, 'audio')) }>
                        <ContextMenuIcon src = { IconMicDisabled } />
                        <span>{t('participantsPane.actions.muteMic')}</span>
                    </ContextMenuItem>
                ) }
                { !occupant.videoMuted && (
                    <ContextMenuItem
                        onClick = { runAction(requestParticipantMute(occupant.id, 'video')) }>
                        <ContextMenuIcon src = { IconCameraEmptyDisabled } />
                        <span>{t('participantsPane.actions.muteCamera')}</span>
                    </ContextMenuItem>
                ) }
                <ContextMenuItem
                    onClick = { runAction(moveAction(occupant.id)) }>
                    <ContextMenuIcon src = { moveIcon } />
                    <span>{t(moveLabelKey)}</span>
                </ContextMenuItem>
                { !isModerator && (
                    <ContextMenuItem
                        color = '#D3223A'
                        onClick = { runAction(requestParticipantKick(occupant.id)) }>
                        <ContextMenuIcon src = { IconCloseCircle } />
                        <span>{t('participantsPane.actions.kick')}</span>
                    </ContextMenuItem>
                ) }
            </ContextMenuItemGroup>
        </ParticipantContextMenu>
    );
};
